import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { buildDemoIntraday, buildSyntheticIntraday } from "../lib/motion/path";
import { buildTargetScale, createInitialScale, stepScale, type ScaleState } from "../lib/motion/scale";
import { getTimelineState, timeline, weeklyTimeline } from "../lib/motion/timeline";
import { renderDailyFrame, renderWeeklyFrame } from "../lib/motion/renderer";
import { DEFAULT_RENDER_SETTINGS, type MotionRenderSettings } from "../lib/motion/render-settings";
import type { MarketSnapshot, WeeklySummary } from "../lib/market-data/types";
import { loadSkinAssets, type LoadedSkinAssets } from "../lib/skins/assets";
import type { MotionSkin } from "../lib/skins/types";
import type { ScenarioId } from "../lib/scenarios/demo";

export type MotionCanvasHandle = {
  replay: () => void;
  getCanvas: () => HTMLCanvasElement | null;
};

type Props = {
  snapshot: MarketSnapshot;
  weekly: WeeklySummary | null;
  skin: MotionSkin;
  scenarioId: ScenarioId;
  mode: "daily" | "weekly";
  settings?: MotionRenderSettings;
  staticPreview?: boolean;
  onStatus: (status: string) => void;
};

export const MotionCanvas = forwardRef<MotionCanvasHandle, Props>(function MotionCanvas(props, ref) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number>(0);
  const doneTimerRef = useRef<number>(0);
  const startRef = useRef<number>(0);
  const scaleRef = useRef<ScaleState>(createInitialScale(props.snapshot.open, props.snapshot.previousClose));
  const settings = props.settings ?? DEFAULT_RENDER_SETTINGS;
  const [assets, setAssets] = useState<LoadedSkinAssets>({});

  function getPoints() {
    if (props.scenarioId === "surge") return buildDemoIntraday(props.snapshot, "surge").points;
    if (props.scenarioId === "crash") return buildDemoIntraday(props.snapshot, "crash").points;
    return buildSyntheticIntraday(props.snapshot, `${props.snapshot.symbolId}-${props.snapshot.date}-${props.scenarioId}`).points;
  }

  function drawStill() {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    if (props.mode === "weekly") {
      if (!props.weekly) {
        props.onStatus("週間データを読み込み中");
        return;
      }
      const initial = createInitialScale(props.weekly.open, props.weekly.previousWeekClose);
      const scale = buildTargetScale(props.weekly.points, props.weekly.points.length, props.weekly.previousWeekClose, initial.halfRange);
      renderWeeklyFrame(ctx, props.weekly, props.skin, 1, scale, props.weekly.points.length, 1, 1, settings, assets);
      props.onStatus("週間プレビュー完了");
      return;
    }
    const points = getPoints();
    const initial = createInitialScale(props.snapshot.open, props.snapshot.previousClose);
    const scale = buildTargetScale(points, points.length, props.snapshot.previousClose, initial.halfRange);
    renderDailyFrame({
      ctx,
      snapshot: props.snapshot,
      skin: props.skin,
      points,
      scale,
      visibleCount: points.length,
      finishProgress: 1,
      holdProgress: 1,
      motionProgress: 1,
      scenarioLabel: props.scenarioId,
      settings,
      assets
    });
    props.onStatus("再生完了");
  }

  function replay() {
    window.cancelAnimationFrame(rafRef.current);
    window.clearTimeout(doneTimerRef.current);
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    if (props.mode === "weekly") {
      if (!props.weekly) {
        props.onStatus("週間データがありません");
        return;
      }
      scaleRef.current = createInitialScale(props.weekly.open, props.weekly.previousWeekClose);
      const initialHalfRange = scaleRef.current.halfRange;
      startRef.current = performance.now();
      props.onStatus("週間プレビュー再生中");
      doneTimerRef.current = window.setTimeout(() => props.onStatus("週間プレビュー完了"), weeklyTimeline.totalDurationMs + 120);
      const render = (now: number) => {
        const elapsed = now - startRef.current;
        const state = getTimelineState(elapsed, (props.weekly as WeeklySummary).points.length);
        const target = buildTargetScale((props.weekly as WeeklySummary).points, state.visibleCount, (props.weekly as WeeklySummary).previousWeekClose, initialHalfRange);
        scaleRef.current = stepScale(scaleRef.current, target);
        renderWeeklyFrame(ctx, props.weekly as WeeklySummary, props.skin, state.timelineProgress, scaleRef.current, state.visibleCount, state.finishProgress, state.holdProgress, settings, assets);
        if (elapsed < weeklyTimeline.totalDurationMs) rafRef.current = window.requestAnimationFrame(render);
        else {
          window.clearTimeout(doneTimerRef.current);
          props.onStatus("週間プレビュー完了");
        }
      };
      rafRef.current = window.requestAnimationFrame(render);
      return;
    }
    const points = getPoints();
    scaleRef.current = createInitialScale(props.snapshot.open, props.snapshot.previousClose);
    const initialHalfRange = scaleRef.current.halfRange;
    startRef.current = performance.now();
    props.onStatus("再生中");
    doneTimerRef.current = window.setTimeout(() => props.onStatus("再生完了"), timeline.totalDurationMs + 120);

    const render = (now: number) => {
      const elapsed = now - startRef.current;
      const state = getTimelineState(elapsed, points.length);
      const target = buildTargetScale(points, state.visibleCount, props.snapshot.previousClose, initialHalfRange);
      scaleRef.current = stepScale(scaleRef.current, target);
      renderDailyFrame({
        ctx,
        snapshot: props.snapshot,
        skin: props.skin,
        points,
        scale: scaleRef.current,
        visibleCount: state.visibleCount,
        finishProgress: state.finishProgress,
        holdProgress: state.holdProgress,
        motionProgress: state.holdProgress > 0
          ? (timeline.lineDurationMs + timeline.settleDurationMs + state.holdProgress * 120) / timeline.totalDurationMs
          : state.timelineProgress,
        scenarioLabel: props.scenarioId,
        settings,
        assets
      });
      if (elapsed < timeline.totalDurationMs) rafRef.current = window.requestAnimationFrame(render);
      else {
        window.clearTimeout(doneTimerRef.current);
        props.onStatus("再生完了");
      }
    };
    rafRef.current = window.requestAnimationFrame(render);
  }

  useImperativeHandle(ref, () => ({
    replay,
    getCanvas: () => canvasRef.current
  }));

  useEffect(() => {
    let cancelled = false;
    setAssets({});
    void loadSkinAssets(props.skin).then((loaded) => {
      if (!cancelled) setAssets(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, [props.skin]);

  useEffect(() => {
    if (props.staticPreview) {
      drawStill();
      return () => {
        window.cancelAnimationFrame(rafRef.current);
        window.clearTimeout(doneTimerRef.current);
      };
    }
    replay();
    return () => {
      window.cancelAnimationFrame(rafRef.current);
      window.clearTimeout(doneTimerRef.current);
    };
    // The renderer intentionally restarts only when render inputs change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.snapshot, props.skin, props.scenarioId, props.mode, props.weekly, props.settings, props.staticPreview, assets]);

  useEffect(() => {
    drawStill();
    // Initial paint before the first animation frame is enough here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <canvas ref={canvasRef} className="motion-canvas" width={1080} height={1080} />;
});
